const { TokenType, Keywords } = require('./keywords');

class Lexer {
    constructor(source, languageConfig) {
        this.source = source;
        this.config = languageConfig;
        this.position = 0;
        this.line = 1;
        this.column = 1;
        this.tokens = [];

        // Create reverse mapping for easier keyword lookup: Human Keyword -> Internal Keyword ID
        this.keywordMap = {};
        this.multiWordMap = {}; // First word -> List of full keyword parts and ID

        for (const [key, value] of Object.entries(languageConfig)) {
            const parts = value.trim().split(/\s+/);
            if (parts.length > 1) {
                const first = parts[0];
                if (!this.multiWordMap[first]) {
                    this.multiWordMap[first] = [];
                }
                this.multiWordMap[first].push({ id: key, parts: parts });
            } else {
                this.keywordMap[value] = key;
            }
        }

        // Sort multi-word matches by length descending to match longest first
        for (const key in this.multiWordMap) {
            this.multiWordMap[key].sort((a, b) => b.parts.length - a.parts.length);
        }
    }

    tokenize() {
        while (this.position < this.source.length) {
            const char = this.source[this.position];

            if (/\s/.test(char)) {
                this.handleWhitespace(char);
                continue;
            }

            if (this.isDigit(char) || (char === '.' && this.position + 1 < this.source.length && this.isDigit(this.source[this.position + 1]))) {
                this.readNumber();
                continue;
            }

            if (this.isIdentifierStart(char)) {
                this.readIdentifier();
                continue;
            }

            if (char === '"' || char === "'") {
                this.readString(char);
                continue;
            }

            // Handle comments
            if (char === '/' && this.position + 1 < this.source.length) {
                const nextChar = this.source[this.position + 1];
                if (nextChar === '/') {
                    this.skipSingleLineComment();
                    continue;
                } else if (nextChar === '*') {
                    this.skipMultiLineComment();
                    continue;
                }
            }

            // Handle operators and punctuation
            if (this.handleSymbol()) {
                continue;
            }

            throw new Error(`Unexpected character: ${char} at line ${this.line}, column ${this.column}`);
        }

        this.tokens.push({ type: TokenType.EOF, value: null, line: this.line });
        return this.tokens;
    }

    handleWhitespace(char) {
        if (char === '\n') {
            this.line++;
            this.column = 1;
        } else {
            this.column++;
        }
        this.position++;
    }

    isDigit(char) {
        return /[0-9]/.test(char);
    }

    readNumber() {
        let start = this.position;
        let hasDecimal = false;

        while (this.position < this.source.length) {
            const char = this.source[this.position];
            if (this.isDigit(char)) {
                this.position++;
                this.column++;
            } else if (char === '.' && !hasDecimal && this.isDigit(this.source[this.position + 1])) {
                hasDecimal = true;
                this.position++;
                this.column++;
            } else {
                break;
            }
        }
        const value = this.source.substring(start, this.position);
        this.tokens.push({ type: TokenType.NUMBER, value: Number(value), line: this.line });
    }

    isIdentifierStart(char) {
        // Allow unicode characters for identifiers
        return /[a-zA-Z_\u00C0-\uFFFF]/.test(char);
    }

    isIdentifierPart(char) {
        return /[a-zA-Z0-9_\u00C0-\uFFFF]/.test(char);
    }

    readIdentifier() {
        // Read the first word
        const start = this.position;
        const word = this.readWord();

        // Check for multi-word keywords starting with this word
        if (this.multiWordMap[word]) {
            for (const item of this.multiWordMap[word]) {
                if (this.matchMultiWord(item.parts)) {
                    this.tokens.push({
                        type: TokenType.KEYWORD,
                        value: item.id,
                        raw: item.parts.join(' '),
                        line: this.line
                    });
                    return;
                }
            }
        }

        // Single word check
        if (this.keywordMap[word]) {
            this.tokens.push({
                type: TokenType.KEYWORD,
                value: this.keywordMap[word],
                raw: word,
                line: this.line
            });
        } else {
            this.tokens.push({ type: TokenType.IDENTIFIER, value: word, line: this.line });
        }
    }

    readWord() {
        let start = this.position;
        while (this.position < this.source.length && this.isIdentifierPart(this.source[this.position])) {
            this.position++;
            this.column++;
        }
        return this.source.substring(start, this.position);
    }

    matchMultiWord(parts) {
        // We already matched parts[0] (current word)
        // We need to peek ahead to match parts[1]...parts[n]

        let savedPos = this.position;
        let savedLine = this.line;
        let savedCol = this.column;

        for (let i = 1; i < parts.length; i++) {
            // Skip whitespace
            this.skipWhitespace();

            if (this.position >= this.source.length) {
                this.restore(savedPos, savedLine, savedCol);
                return false;
            }

            const nextWord = this.readWord();
            if (nextWord !== parts[i]) {
                this.restore(savedPos, savedLine, savedCol);
                return false;
            }
        }

        return true;
    }

    skipWhitespace() {
        while (this.position < this.source.length && /\s/.test(this.source[this.position])) {
            this.handleWhitespace(this.source[this.position]);
        }
    }

    skipSingleLineComment() {
        this.position += 2; // Skip //
        this.column += 2;
        while (this.position < this.source.length && this.source[this.position] !== '\n') {
            this.position++;
            this.column++;
        }
    }

    skipMultiLineComment() {
        this.position += 2; // Skip /*
        this.column += 2;
        while (this.position < this.source.length) {
            if (this.source[this.position] === '*' && this.source[this.position + 1] === '/') {
                this.position += 2;
                this.column += 2;
                return;
            }
            if (this.source[this.position] === '\n') {
                this.line++;
                this.column = 1;
            } else {
                this.column++;
            }
            this.position++;
        }
        throw new Error(`Unterminated multi-line comment starting at line ${this.line}`);
    }

    restore(pos, line, col) {
        this.position = pos;
        this.line = line;
        this.column = col;
    }

    readString(quoteType) {
        this.position++; // Skip opening quote
        this.column++;
        let result = '';

        while (this.position < this.source.length && this.source[this.position] !== quoteType) {
            let char = this.source[this.position];
            if (char === '\\') {
                this.position++;
                this.column++;
                if (this.position >= this.source.length) {
                    throw new Error(`Unterminated escape sequence at line ${this.line}`);
                }
                const escapeChar = this.source[this.position];
                switch (escapeChar) {
                    case 'n': result += '\n'; break;
                    case 't': result += '\t'; break;
                    case 'r': result += '\r'; break;
                    case 'b': result += '\b'; break;
                    case 'f': result += '\f'; break;
                    case '\\': result += '\\'; break;
                    case '"': result += '"'; break;
                    case "'": result += "'"; break;
                    default:
                        // For unknown escape, keep the character
                        result += escapeChar;
                }
            } else if (char === '\n') {
                this.line++;
                this.column = 1;
                result += char;
            } else {
                result += char;
                this.column++;
            }
            this.position++;
        }

        if (this.position >= this.source.length) {
            throw new Error(`Unterminated string starting at line ${this.line}`);
        }

        this.position++; // Skip closing quote
        this.column++;

        this.tokens.push({ type: TokenType.STRING, value: result, line: this.line });
    }

    handleSymbol() {
        const char = this.source[this.position];
        const nextChar = this.position + 1 < this.source.length ? this.source[this.position + 1] : null;

        // Two character operators
        if (char === '=' && nextChar === '=') {
            this.addToken(TokenType.OPERATOR, '==');
            this.position += 2; this.column += 2; return true;
        }
        if (char === '!' && nextChar === '=') {
            this.addToken(TokenType.OPERATOR, '!=');
            this.position += 2; this.column += 2; return true;
        }
        if (char === '>' && nextChar === '=') {
            this.addToken(TokenType.OPERATOR, '>=');
            this.position += 2; this.column += 2; return true;
        }
        if (char === '<' && nextChar === '=') {
            this.addToken(TokenType.OPERATOR, '<=');
            this.position += 2; this.column += 2; return true;
        }
        if (char === '+' && nextChar === '=') { // For += assignment/operator
            this.addToken(TokenType.OPERATOR, '+=');
            this.position += 2; this.column += 2; return true;
        }

        // Single character operators/punctuation
        if ('+-*/%=<>!'.includes(char)) {
            this.addToken(TokenType.OPERATOR, char);
            this.position++; this.column++; return true;
        }

        if ('(){}[];,'.includes(char) || char === '.') {
            this.addToken(TokenType.PUNCTUATION, char);
            this.position++; this.column++; return true;
        }

        return false;
    }

    addToken(type, value) {
        this.tokens.push({ type, value, line: this.line });
    }
}

module.exports = Lexer;
